import { RefObject, useCallback, useEffect, useRef } from "react";
import type { MapRef } from "react-map-gl/maplibre";

export interface TileCoordinates {
  x: number;
  y: number;
  z: number;
}

interface SourceRetryState {
  attemptsByTile: Map<string, number>;
  sourceAttempts: number;
  queuedTiles: Map<string, TileCoordinates>;
  retryAll: boolean;
  timer?: number;
  baseDelayMs: number;
  waitingForOnline: boolean;
}

interface ScheduleTileRetryOptions {
  sourceId: string;
  status: number;
  tile?: TileCoordinates;
}

const MAX_RETRY_ATTEMPTS = 3;
const REQUEST_TIMEOUT_DELAY_MS = 1000;
const SERVER_ERROR_DELAY_MS = 2000;
const RATE_LIMIT_DELAY_MS = 5000;

const getTileKey = ({ x, y, z }: TileCoordinates) => `${z}/${x}/${y}`;

const createRetryState = (): SourceRetryState => ({
  attemptsByTile: new Map(),
  sourceAttempts: 0,
  queuedTiles: new Map(),
  retryAll: false,
  baseDelayMs: 0,
  waitingForOnline: false,
});

const queueRetry = (state: SourceRetryState, tile?: TileCoordinates) => {
  if (tile) {
    const tileKey = getTileKey(tile);
    if ((state.attemptsByTile.get(tileKey) ?? 0) >= MAX_RETRY_ATTEMPTS) {
      return false;
    }
    state.queuedTiles.set(tileKey, tile);
    return true;
  }

  if (state.sourceAttempts >= MAX_RETRY_ATTEMPTS) {
    return false;
  }
  state.retryAll = true;
  return true;
};

const getBaseDelay = (status: number) => {
  if (status === 429) {
    return RATE_LIMIT_DELAY_MS;
  }
  if (status >= 500 && status <= 599) {
    return SERVER_ERROR_DELAY_MS;
  }
  return REQUEST_TIMEOUT_DELAY_MS;
};

export const isRetryableTileStatus = (status: number | undefined) =>
  status === 0 ||
  status === 408 ||
  status === 429 ||
  (status !== undefined && status >= 500 && status <= 599);

/**
 * Batches retries per source and refreshes only failed tiles when MapLibre
 * provides their coordinates. The MapLibre refresh API works for raster and
 * vector tile sources alike.
 */
export const useMapTileRetry = (mapRef: RefObject<MapRef>) => {
  const retryStatesRef = useRef<Map<string, SourceRetryState>>(new Map());

  const flushRetries = useCallback(
    (sourceId: string) => {
      const state = retryStatesRef.current.get(sourceId);
      if (!state) {
        return;
      }

      state.timer = undefined;
      state.baseDelayMs = 0;
      state.waitingForOnline = false;

      const { retryAll } = state;
      const queuedTiles = Array.from(state.queuedTiles.values());
      state.retryAll = false;
      state.queuedTiles.clear();

      const map = mapRef.current?.getMap();
      if (!map) {
        return;
      }

      if (retryAll) {
        if (state.sourceAttempts >= MAX_RETRY_ATTEMPTS) {
          return;
        }
        state.sourceAttempts += 1;
        try {
          map.refreshTiles(sourceId);
        } catch {
          retryStatesRef.current.delete(sourceId);
        }
        return;
      }

      const tilesToRetry = queuedTiles.filter((tile) => {
        const key = getTileKey(tile);
        const attempts = state.attemptsByTile.get(key) ?? 0;
        if (attempts >= MAX_RETRY_ATTEMPTS) {
          return false;
        }
        state.attemptsByTile.set(key, attempts + 1);
        return true;
      });

      if (tilesToRetry.length) {
        try {
          map.refreshTiles(sourceId, tilesToRetry);
        } catch {
          retryStatesRef.current.delete(sourceId);
        }
      }
    },
    [mapRef]
  );

  const scheduleTileRetry = useCallback(
    ({ sourceId, status, tile }: ScheduleTileRetryOptions) => {
      const isOffline =
        typeof navigator !== "undefined" && navigator.onLine === false;

      // A status of 0 while the browser reports being online is commonly CORS,
      // DNS, or a malformed URL. Retrying it continuously would not help.
      if (status === 0 && !isOffline) {
        return;
      }

      let state = retryStatesRef.current.get(sourceId);
      if (!state) {
        state = createRetryState();
        retryStatesRef.current.set(sourceId, state);
      }

      if (!queueRetry(state, tile)) {
        return;
      }

      if (isOffline) {
        state.waitingForOnline = true;
        if (state.timer !== undefined) {
          window.clearTimeout(state.timer);
          state.timer = undefined;
        }
        return;
      }

      const baseDelayMs = getBaseDelay(status);
      if (state.timer !== undefined && state.baseDelayMs >= baseDelayMs) {
        return;
      }
      if (state.timer !== undefined) {
        window.clearTimeout(state.timer);
      }

      state.baseDelayMs = Math.max(state.baseDelayMs, baseDelayMs);
      const attempts = tile
        ? (state.attemptsByTile.get(getTileKey(tile)) ?? 0)
        : state.sourceAttempts;
      const delayMs = state.baseDelayMs * 2 ** attempts;
      state.timer = window.setTimeout(() => flushRetries(sourceId), delayMs);
    },
    [flushRetries]
  );

  const resetTileRetries = useCallback((sourceId: string) => {
    const state = retryStatesRef.current.get(sourceId);
    if (state?.timer !== undefined) {
      window.clearTimeout(state.timer);
    }
    retryStatesRef.current.delete(sourceId);
  }, []);

  useEffect(() => {
    const handleOnline = () => {
      retryStatesRef.current.forEach((state, sourceId) => {
        if (!state.waitingForOnline || state.timer !== undefined) {
          return;
        }
        state.waitingForOnline = false;
        state.timer = window.setTimeout(() => flushRetries(sourceId), 0);
      });
    };

    window.addEventListener("online", handleOnline);
    const retryStates = retryStatesRef.current;

    return () => {
      window.removeEventListener("online", handleOnline);
      retryStates.forEach((state) => {
        if (state.timer !== undefined) {
          window.clearTimeout(state.timer);
        }
      });
      retryStates.clear();
    };
  }, [flushRetries]);

  return { scheduleTileRetry, resetTileRetries };
};
